import { OlympCaptchaDestination, OlympCaptchaType } from '@/Olymp/types';
import { useAuthContext } from '@/context/AuthContext';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useRecaptcha = (type: OlympCaptchaDestination) => {
  const [value, setValue] = useState<string>();
  const [error, setError] = useState<boolean>();
  const { captchaState } = useAuthContext();

  const visible = useMemo(() => {
    if (!captchaState.fetched) {
      return false;
    }
    return captchaState.state?.destinations?.includes(type);
  }, [type, captchaState.fetched, captchaState.state?.destinations]);

  const key = useMemo(() => {
    if (!visible) {
      return '';
    }
    const recaptcha = captchaState.state?.types.at(0);
    if (!recaptcha) {
      return '';
    }
    return recaptcha.key;
  }, [visible, captchaState.state?.types]);

  const version = useMemo(() => {
    if (!visible) {
      return '';
    }
    const recaptcha = captchaState.state?.types.at(0);
    if (!recaptcha) {
      return '';
    }
    return recaptcha.version;
  }, [visible, captchaState.state?.types]);

  const handleChange = useCallback((value: string) => {
    setValue(value);
    setError(false);
  }, []);

  const isValid = useCallback(() => {
    if (!visible || value) {
      setError(false);
      return true;
    }
    setError(true);
    return false;
  }, [visible, value]);

  useEffect(() => {
    if (captchaState.timestamp) {
      setValue(undefined);
    }
  }, [captchaState.timestamp]);

  return useMemo(
    () => ({
      value,
      key,
      version,
      timestamp: captchaState.timestamp,
      error,
      visible,
      isValid,
      onChange: handleChange,
    }),
    [value, key, captchaState.timestamp, error, visible, isValid, handleChange],
  );
};
